import { useState } from 'react';
import { Link } from 'react-router-dom';
import { CalendarDays, SlidersHorizontal, Zap } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { useAuth } from '../../hooks/useAuth';
import { useSprintList, useCreateSprint, useDeleteSprint } from '../../api/sprints';
import { useTeamList } from '../../api/teams';
import { applyServerErrors } from '../../lib/formErrors';
import PageHeader from '../../components/layout/PageHeader';
import Badge from '../../components/ui/Badge';
import Button from '../../components/ui/Button';
import { CardListSkeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/ui/empty-state';
import ErrorState from '../../components/ui/ErrorState';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { formatDate } from '../../lib/dates';
import type { SprintDto, SprintStatus } from '../../types/api';

const SELECT_CLS     = 'h-9 w-full rounded-md border border-input bg-background px-3 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring';
const FILTER_SELECT  = 'h-8 rounded-md border-0 bg-transparent pl-2 pr-7 text-sm focus:outline-none focus:ring-0 cursor-pointer text-foreground';

const STATUS_VARIANT: Record<SprintStatus, 'green' | 'gray' | 'blue'> = {
  Planning: 'blue', Active: 'green', Completed: 'gray',
};

interface CreateForm {
  teamId: string; name: string; goal: string; startDate: string; endDate: string;
}

function SprintRow({ sprint, teamName, canDelete, onDelete }: {
  sprint: SprintDto; teamName: string; canDelete: boolean;
  onDelete: (id: string, name: string) => void;
}) {
  return (
    <div className="flex items-start justify-between px-5 py-4 hover:bg-muted/40 transition-colors">
      <Link to={`/sprints/${sprint.id}`} className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-medium text-foreground">{sprint.name}</span>
          <Badge label={sprint.status} variant={STATUS_VARIANT[sprint.status]} />
        </div>
        <p className="mt-0.5 text-sm text-muted-foreground">
          {teamName} · {formatDate(sprint.startDate)} – {formatDate(sprint.endDate)}
        </p>
        {sprint.goal && <p className="mt-1 truncate text-xs text-muted-foreground/70">{sprint.goal}</p>}
      </Link>
      <div className="ml-4 flex shrink-0 items-center gap-2">
        {canDelete && sprint.status === 'Planning' && (
          <button
            onClick={(e) => { e.preventDefault(); onDelete(sprint.id, sprint.name); }}
            className="text-xs text-destructive hover:underline"
          >
            Delete
          </button>
        )}
      </div>
    </div>
  );
}

export default function SprintsPage() {
  const { can } = useAuth();
  const [teamFilter, setTeamFilter] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<{ id: string; name: string } | null>(null);

  const { data: sprints, isLoading, error, refetch } = useSprintList(teamFilter || undefined);
  const { data: teams } = useTeamList();
  const createSprint = useCreateSprint();
  const deleteSprint = useDeleteSprint();

  const { register, handleSubmit, setError, reset, formState: { errors } } = useForm<CreateForm>();

  const onSubmit = handleSubmit((values) => {
    createSprint.mutate(
      { teamId: values.teamId, name: values.name, goal: values.goal || undefined, startDate: values.startDate, endDate: values.endDate },
      {
        onSuccess: () => { toast.success('Sprint created.'); reset(); setShowCreate(false); },
        onError:   (e) => applyServerErrors(e, setError),
      },
    );
  });

  const getTeamName = (teamId: string) => teams?.find((t) => t.id === teamId)?.name ?? '—';

  if (isLoading) return <CardListSkeleton count={5} />;
  if (error)     return <ErrorState onRetry={refetch} />;

  return (
    <div className="max-w-2xl">
      <PageHeader
        title="Sprints"
        actions={
          can('team_lead') ? (
            <Button size="sm" onClick={() => setShowCreate((v) => !v)}>New sprint</Button>
          ) : undefined
        }
      />

      <div className="mb-4 flex items-center gap-2 rounded-xl border bg-card px-3 py-2 shadow-sm">
        <SlidersHorizontal className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        <div className="flex items-center">
          <span className="px-2 text-xs font-medium text-muted-foreground">Team</span>
          <select value={teamFilter} onChange={(e) => setTeamFilter(e.target.value)} className={FILTER_SELECT}>
            <option value="">All</option>
            {teams?.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </div>
      </div>

      {showCreate && (
        <Card className="mb-5 overflow-hidden">
          <div className="flex items-center gap-2.5 border-b border-border px-5 py-4">
            <div className="flex h-7 w-7 items-center justify-center rounded-md bg-primary/10">
              <Zap className="h-4 w-4 text-primary" />
            </div>
            <p className="text-sm font-semibold text-foreground">New sprint</p>
          </div>
          <form onSubmit={onSubmit}>
            <div className="space-y-4 px-5 py-5">
              <div className="space-y-1.5">
                <Label>Team <span className="text-destructive">*</span></Label>
                <select {...register('teamId', { required: 'Team is required' })} className={SELECT_CLS}>
                  <option value="">Select a team</option>
                  {teams?.filter((t) => t.isActive).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
                {errors.teamId && <p className="text-xs text-destructive">{errors.teamId.message}</p>}
              </div>
              <div className="space-y-1.5">
                <Label>Name <span className="text-destructive">*</span></Label>
                <Input {...register('name', { required: 'Name is required' })} placeholder="e.g. Sprint 12" />
                {errors.name && <p className="text-xs text-destructive">{errors.name.message}</p>}
              </div>
              <div className="space-y-1.5">
                <Label>Goal</Label>
                <Input {...register('goal')} placeholder="Optional sprint goal" />
              </div>
            </div>

            <div className="flex items-center gap-2.5 border-y border-border px-5 py-4">
              <div className="flex h-7 w-7 items-center justify-center rounded-md bg-violet-50 dark:bg-violet-950/40">
                <CalendarDays className="h-4 w-4 text-violet-600 dark:text-violet-400" />
              </div>
              <p className="text-sm font-semibold text-foreground">Dates</p>
            </div>
            <div className="grid grid-cols-1 gap-4 px-5 py-5 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label>Start date <span className="text-destructive">*</span></Label>
                <Input type="date" {...register('startDate', { required: 'Start date is required' })} />
                {errors.startDate && <p className="text-xs text-destructive">{errors.startDate.message}</p>}
              </div>
              <div className="space-y-1.5">
                <Label>End date <span className="text-destructive">*</span></Label>
                <Input type="date" {...register('endDate', { required: 'End date is required' })} />
                {errors.endDate && <p className="text-xs text-destructive">{errors.endDate.message}</p>}
              </div>
            </div>

            <div className="flex gap-2 border-t border-border bg-muted/30 px-5 py-3">
              <Button size="sm" type="submit" loading={createSprint.isPending}>Create</Button>
              <Button size="sm" variant="ghost" type="button" onClick={() => { setShowCreate(false); reset(); }}>Cancel</Button>
            </div>
          </form>
        </Card>
      )}

      <Card className="divide-y divide-border overflow-hidden p-0">
        {sprints?.length === 0 ? (
          <EmptyState icon={Zap} title="No sprints yet" description="Create a sprint to start tracking team velocity." />
        ) : (
          <>
            <div className="flex items-center justify-between px-4 py-2.5">
              <span className="text-xs text-muted-foreground">
                {sprints?.length} sprint{sprints?.length !== 1 ? 's' : ''}
                {teamFilter && <span className="ml-1 text-primary">· filtered</span>}
              </span>
            </div>
            {sprints?.map((s) => (
              <SprintRow key={s.id} sprint={s} teamName={getTeamName(s.teamId)} canDelete={can('head_of_rd')} onDelete={(id, name) => setConfirmDelete({ id, name })} />
            ))}
          </>
        )}
      </Card>

      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-sm rounded-xl bg-card border border-border p-6 shadow-xl">
            <h3 className="text-base font-semibold text-foreground">Delete sprint?</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              <strong className="text-foreground">{confirmDelete.name}</strong> will be permanently deleted.
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(null)}>Cancel</Button>
              <Button size="sm" variant="danger" loading={deleteSprint.isPending}
                onClick={() => deleteSprint.mutate(confirmDelete.id, {
                  onSuccess: () => { toast.success('Sprint deleted.'); setConfirmDelete(null); },
                  onError:   () => { toast.error('Failed to delete sprint.'); setConfirmDelete(null); },
                })}
              >
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
